import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, Ban, XCircle, ExternalLink } from 'lucide-react';
import {
  PageHeader,
  DashboardCard,
  StatusBadge,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import {
  useInvoice,
  useKoshLabels,
  useCustomerNameMap,
  useInvoiceTransition,
} from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { invoiceStatusVariants } from '@/config/kosh-status';
import { formatMoney, shortDate } from '@/lib/format';
import type { KoshInvoice, KoshInvoiceStatus } from '@/types/kosh';

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useInvoice(id);
  const { label } = useKoshLabels();
  const nameMap = useCustomerNameMap();
  const transition = useInvoiceTransition();

  const act = (to: KoshInvoiceStatus) => id && transition.mutate({ id, to });

  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(inv: KoshInvoice) => (
          <>
            <PageHeader
              eyebrow={
                <Link to="/invoices" className="inline-flex items-center gap-1 hover:text-foreground">
                  <ArrowLeft className="h-3.5 w-3.5" /> {label('invoice')}s
                </Link>
              }
              title={inv.invoiceNumber}
              meta={<StatusBadge status={inv.status} variants={invoiceStatusVariants} />}
              description={
                <span>
                  {nameMap.get(inv.customerId) ?? inv.customerId} · {inv.sourceType.toLowerCase()}
                  {inv.sourceType === 'ORDER' && (
                    <>
                      {' · '}
                      <a
                        href="https://zafroi.com/invoices"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> View in Operations
                      </a>
                    </>
                  )}
                </span>
              }
              actions={
                <div className="flex gap-2">
                  {inv.status === 'DRAFT' && (
                    <>
                      <Button size="sm" disabled={transition.isPending} onClick={() => act('ISSUED')}>
                        <Send className="h-4 w-4" /> Issue
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={transition.isPending}
                        onClick={() => act('CANCELLED')}
                      >
                        <XCircle className="h-4 w-4" /> Cancel
                      </Button>
                    </>
                  )}
                  {(inv.status === 'ISSUED' || inv.status === 'OVERDUE') && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={transition.isPending}
                      onClick={() => act('VOID')}
                    >
                      <Ban className="h-4 w-4" /> Void
                    </Button>
                  )}
                </div>
              }
            />

            <DashboardCard title="Line items" flushBody>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inv.lineItems.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{l.description}</TableCell>
                      <TableCell className="text-right tabular-nums">{l.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(l.unitPrice, inv.currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(l.tax ?? 0, inv.currency)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(l.amount, inv.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DashboardCard>

            <div className="ml-auto max-w-xs space-y-1.5 text-[13px]">
              <Row k="Subtotal" v={formatMoney(inv.subTotal, inv.currency)} />
              <Row k="Tax" v={formatMoney(inv.taxTotal, inv.currency)} />
              <div className="flex justify-between border-t border-border pt-1.5 text-[15px] font-bold">
                <span>Total</span>
                <span className="tabular-nums">{formatMoney(inv.total, inv.currency)}</span>
              </div>
              {inv.amountSettled > 0 && (
                <Row k="Settled" v={formatMoney(inv.amountSettled, inv.currency)} />
              )}
              <Row k="Issued" v={shortDate(inv.issueDate)} />
              <Row k="Due" v={shortDate(inv.dueDate)} />
            </div>
            {inv.notes && <p className="text-sm text-muted-foreground">{inv.notes}</p>}
          </>
        )}
      </QueryStateGuard>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className="tabular-nums">{v}</span>
    </div>
  );
}
